import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { X, ChevronLeft, Calendar, TrendingUp, TrendingDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const YogaChecker = ({ onClose, selectedDate, kulaType }) => {
  const [currentYogaData, setCurrentYogaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('current'); // 'current' or 'details'
  const [selectedYoga, setSelectedYoga] = useState(null);
  const [yogaDetails, setYogaDetails] = useState({ upcoming: [], past: [] });
  const [loadingMore, setLoadingMore] = useState(false);

  const yogaInfo = {
    pushpini: { index: 1, name: 'पुष्पिणी योग', desc: 'Vasara and Masa match', color: 'rose' },
    mohini: { index: 2, name: 'मोहिनी योग', desc: 'Vasara and Divasa match', color: 'purple' },
    jayini: { index: 3, name: 'जयिनी योग', desc: 'Vasara and Ghatikodaya match', color: 'blue' },
    kaumari: { index: 4, name: 'कौमारी योग', desc: 'Masa and Divasa match', color: 'green' },
    vimala: { index: 5, name: 'विमला योग', desc: 'Masa and Ghatikodaya match', color: 'amber' },
    shrikari: { index: 6, name: 'श्रीकरी योग', desc: 'Ghatikodaya and Divasa match', color: 'teal' }
  };

  useEffect(() => {
    checkCurrentYogas();
  }, [selectedDate]);

  const checkCurrentYogas = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/devimana/check-yogas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: selectedDate,
          kula_type: kulaType,
          location_name: 'Delhi',
          latitude: 28.6139,
          longitude: 77.209
        })
      });

      const data = await response.json();
      if (data.success) {
        setCurrentYogaData(data);
      }
    } catch (error) {
      console.error('Error checking yogas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadYogaDetails = async (yogaType, direction, limit = 10) => {
    setLoadingMore(true);
    try {
      const response = await fetch(`${API_URL}/api/devimana/find-yogas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          yoga_type: yogaType,
          start_date: selectedDate,
          direction: direction,
          limit: limit,
          kula_type: kulaType
        })
      });

      const data = await response.json();
      if (data.success) {
        if (direction === 'future') {
          setYogaDetails(prev => ({
            ...prev,
            upcoming: [...prev.upcoming, ...data.yogas]
          }));
        } else {
          setYogaDetails(prev => ({
            ...prev,
            past: [...prev.past, ...data.yogas]
          }));
        }
      }
    } catch (error) {
      console.error('Error loading yoga details:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const openYogaDetails = (yogaKey) => {
    setSelectedYoga(yogaKey);
    setYogaDetails({ upcoming: [], past: [] });
    setActiveView('details');
    // Load initial sets
    loadYogaDetails(yogaKey, 'future', 10);
    loadYogaDetails(yogaKey, 'past', 10);
  };

  const getMatchDescription = (yoga) => {
    const letters = yoga.letters;
    const keys = Object.keys(letters);
    if (keys.length === 2) {
      const [first, second] = keys;
      return `${letters[first]} of ${first.charAt(0).toUpperCase() + first.slice(1)} matches ${letters[second]} of ${second.charAt(0).toUpperCase() + second.slice(1)}`;
    }
    return '';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="w-full max-w-2xl m-4">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Checking Yogas...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-4xl my-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {activeView === 'details' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveView('current')}
                  className="text-white hover:bg-white/20"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              )}
              <div className="flex items-center space-x-3">
                {activeView === 'details' && (
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <span className="text-lg font-bold text-white">{yogaInfo[selectedYoga]?.index}</span>
                  </div>
                )}
                <CardTitle className="text-xl sm:text-2xl">
                  {activeView === 'current' ? 'Check Yogas' : yogaInfo[selectedYoga]?.name}
                </CardTitle>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <p className="text-sm text-indigo-100 mt-2">
            {new Date(selectedDate).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </CardHeader>

        <CardContent className="p-4 sm:p-6">
          {activeView === 'current' ? (
            <>
              {/* Current Date Yogas */}
              {currentYogaData?.has_yogas ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-green-800 mb-2">
                      🎉 Active Yogas on This Date
                    </h3>
                    <div className="space-y-3">
                      {currentYogaData.active_yogas.map((yoga, idx) => {
                        const yogaKey = Object.keys(yogaInfo).find(key => yogaInfo[key].name === yoga.name);
                        const yogaIndex = yogaInfo[yogaKey]?.index;
                        return (
                          <div key={idx} className="bg-white rounded-lg p-3 shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                  <span className="text-sm font-bold text-green-700">{yogaIndex}</span>
                                </div>
                                <h4 className="text-lg font-bold text-indigo-800">{yoga.name}</h4>
                              </div>
                              <Badge variant="outline" className="text-green-600">Active</Badge>
                            </div>
                            <p className="text-sm text-gray-600">{getMatchDescription(yoga)}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <Separator className="my-4" />
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center mb-6">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    No Yogas on This Date
                  </h3>
                  <p className="text-sm text-gray-600">
                    Check upcoming or past yogas below
                  </p>
                </div>
              )}

              {/* All Yogas List */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  All Yoga Types
                </h3>
                {Object.entries(yogaInfo).map(([key, info]) => {
                  const isActive = currentYogaData?.active_yogas?.some(y => 
                    y.name === info.name
                  );
                  return (
                    <button
                      key={key}
                      onClick={() => openYogaDetails(key)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all hover:shadow-lg ${
                        isActive
                          ? 'border-green-400 bg-green-50'
                          : 'border-gray-200 bg-white hover:border-indigo-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-sm font-bold text-indigo-700">{info.index}</span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-lg text-gray-800">{info.name}</h4>
                            <p className="text-sm text-gray-600">{info.desc}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {isActive && (
                            <Badge variant="outline" className="text-green-600">
                              Active Today
                            </Badge>
                          )}
                          <ChevronLeft className="h-5 w-5 text-gray-400 rotate-180" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              {/* Individual Yoga Details View */}
              <div className="space-y-6">
                {/* Upcoming Yogas */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-800">Upcoming Occurrences</h3>
                  </div>
                  
                  {yogaDetails.upcoming.length > 0 ? (
                    <div className="space-y-3">
                      {yogaDetails.upcoming.map((yoga, idx) => (
                        <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-gray-800">{yoga.gregorian_date}</h4>
                              <p className="text-sm text-gray-600">{yoga.day_of_week}</p>
                            </div>
                            <Badge variant="outline" className="text-green-600">
                              Day {idx + 1}
                            </Badge>
                          </div>
                          <Separator className="my-2" />
                          <div className="text-sm space-y-1">
                            <p className="text-gray-700">
                              <span className="font-semibold">Matching: </span>
                              {getMatchDescription(yoga.yoga_details)}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-semibold">Vasara:</span> {yoga.vasara}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-semibold">Masa:</span> {yoga.masa}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-semibold">Divasa:</span> {yoga.divasa}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-semibold">Ghatikodaya:</span> {yoga.ghatikodaya}
                            </p>
                          </div>
                        </div>
                      ))}
                      
                      <Button
                        onClick={() => loadYogaDetails(selectedYoga, 'future', 10)}
                        disabled={loadingMore}
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                      >
                        {loadingMore ? 'Loading...' : 'Load More Upcoming'}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">Loading upcoming yogas...</p>
                  )}
                </div>

                <Separator />

                {/* Past Yogas */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <TrendingDown className="h-5 w-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-800">Past Occurrences</h3>
                  </div>
                  
                  {yogaDetails.past.length > 0 ? (
                    <div className="space-y-3">
                      {yogaDetails.past.map((yoga, idx) => (
                        <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-gray-800">{yoga.gregorian_date}</h4>
                              <p className="text-sm text-gray-600">{yoga.day_of_week}</p>
                            </div>
                            <Badge variant="outline" className="text-blue-600">
                              Past
                            </Badge>
                          </div>
                          <Separator className="my-2" />
                          <div className="text-sm space-y-1">
                            <p className="text-gray-700">
                              <span className="font-semibold">Matching: </span>
                              {getMatchDescription(yoga.yoga_details)}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-semibold">Vasara:</span> {yoga.vasara}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-semibold">Masa:</span> {yoga.masa}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-semibold">Divasa:</span> {yoga.divasa}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-semibold">Ghatikodaya:</span> {yoga.ghatikodaya}
                            </p>
                          </div>
                        </div>
                      ))}
                      
                      <Button
                        onClick={() => loadYogaDetails(selectedYoga, 'past', 10)}
                        disabled={loadingMore}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {loadingMore ? 'Loading...' : 'Load More Past'}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">Loading past yogas...</p>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default YogaChecker;
